import React, { Fragment, useCallback, useEffect, useState } from 'react';
import {
    Dialog,
    DialogPanel,
    Transition,
    TransitionChild,
} from '@headlessui/react';
import { List } from 'immutable';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import useAppContext from 'flow/hooks/useAppContext';
import { useModal } from 'flow/contexts/ModalProvider';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectChatMessageByKey } from 'flow/selectors/chat-message-selectors';
import { ChatMessageKey, ChatSessionKey } from 'flow/types/EntityKeys';
import { getAttachmentLink } from 'flow/utils';
import { ChatContent } from 'flow/utils/message-utils';
import ImagePreviewBottomBar from './ImagePreviewBottomBar';
import ImagePreviewTopBar from './ImagePreviewTopBar';

interface Props {
    messageKey: ChatMessageKey;
    sessionKey: ChatSessionKey;
}

const ImagePreviewModal = ({ messageKey, sessionKey }: Props) => {
    const { closeModal } = useModal();
    const { i18next } = useAppContext();
    const [active, setActive] = useState(true);
    const [displayedMessageKey, setDisplayedMessageKey] = useState(messageKey);

    const images = useAppSelector((state) =>
        state.chats.getIn(['content', ChatContent.IMAGES, sessionKey], List()),
    );

    const imageKey = images.findIndex(
        (img) => img.get('key') === displayedMessageKey,
    );
    const totalImages = images.size;
    const canGoToOlderImage = imageKey > 0;
    // No bucket position to step from when the image isn't in this page (imageKey -1).
    const canGoToNewerImage = imageKey !== -1 && imageKey < totalImages - 1;

    const goToOlderImage = useCallback(() => {
        if (canGoToOlderImage) {
            setDisplayedMessageKey(images.getIn([imageKey - 1, 'key']));
        }
    }, [canGoToOlderImage, imageKey, images]);

    const goToNewerImage = useCallback(() => {
        if (canGoToNewerImage) {
            setDisplayedMessageKey(images.getIn([imageKey + 1, 'key']));
        }
    }, [canGoToNewerImage, imageKey, images]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            switch (event.code) {
                case 'Escape':
                    closeModal();
                    break;
                case 'ArrowLeft':
                    event.preventDefault();
                    goToOlderImage();
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    goToNewerImage();
                    break;
            }
        },
        [closeModal, goToNewerImage, goToOlderImage],
    );

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    // Negative imageKey wraps to the bucket's last item in Immutable.js, so
    // fall back to the main message list instead of indexing images (AC-1325).
    const fallbackMessage = useAppSelector((state) =>
        selectChatMessageByKey(state, sessionKey, displayedMessageKey),
    );
    const displayedImage =
        imageKey === -1 ? fallbackMessage : images.get(imageKey);

    if (!displayedImage) return null;

    const attachment = displayedImage.get('attachment');
    const imageLink = getAttachmentLink(sessionKey, attachment);

    return (
        <Transition show={true} as={Fragment}>
            <Dialog
                static={true}
                as="div"
                className="relative z-20"
                onClose={closeModal}
            >
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <DialogPanel
                        className="no-dark fixed inset-0 h-screen w-screen"
                        onMouseEnter={() => setActive(true)}
                        onMouseLeave={() => setActive(false)}
                    >
                        <div className="flex h-full w-full flex-col gap-y-6 bg-black/70 backdrop-blur-sm">
                            <ImagePreviewTopBar
                                image={displayedImage}
                                sessionKey={sessionKey}
                                visible={active}
                            />
                            <div
                                className="relative flex h-[calc(100%-38rem)] w-full grow items-center justify-center"
                                onClick={(e) => {
                                    if (e.target === e.currentTarget) {
                                        closeModal();
                                    }
                                }}
                            >
                                <img
                                    src={imageLink}
                                    className="max-h-full max-w-full object-contain"
                                    alt={attachment.get('fileName')}
                                />

                                {totalImages > 1 && (
                                    <>
                                        {canGoToOlderImage && (
                                            <div className="absolute top-1/2 left-4 -translate-y-1/2 transform">
                                                <IconButton
                                                    onClick={goToOlderImage}
                                                    aria-label={i18next.t(
                                                        'image_preview.navigate_to_older_image',
                                                    )}
                                                    className="flex size-12 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-all hover:bg-black/70"
                                                >
                                                    <Icon
                                                        iconKey="chevron_left"
                                                        size="2rem"
                                                    />
                                                </IconButton>
                                            </div>
                                        )}

                                        {canGoToNewerImage && (
                                            <div className="absolute top-1/2 right-4 -translate-y-1/2 transform">
                                                <IconButton
                                                    onClick={goToNewerImage}
                                                    aria-label={i18next.t(
                                                        'image_preview.navigate_to_newer_image',
                                                    )}
                                                    className="flex size-12 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-all hover:bg-black/70"
                                                >
                                                    <Icon
                                                        iconKey="chevron_right"
                                                        size="2rem"
                                                    />
                                                </IconButton>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            <ImagePreviewBottomBar
                                fileName={attachment.get('fileName')}
                                visible={active}
                            />
                        </div>
                    </DialogPanel>
                </TransitionChild>
            </Dialog>
        </Transition>
    );
};

export default ImagePreviewModal;
